import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

export default function ModernHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Főoldal', path: '/' },
    { name: 'Rólunk', path: '/about' },
    { name: 'Munkáink', path: '/works' },
    { name: 'Kapcsolat', path: '/contact' },
  ];

  return (
    <>
      {/* Top bar - visible only on desktop */}
      <div className="hidden md:block bg-[var(--color-text-primary)] text-white text-xs">
        <div className="mx-auto max-w-[var(--container-max)] px-4 py-2 flex items-center justify-end gap-6">
          <a href="https://www.facebook.com/goncz.sandor.7" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[var(--color-accent)] transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span>Facebook</span>
          </a>
          <a href="tel:06704179210" className="flex items-center gap-2 hover:text-[var(--color-accent)] transition-colors">
            <span>📞</span>
            <span>06 (70) 417 9210</span>
          </a>
          <a href="mailto:xlbutorbolt@gmail.com" className="flex items-center gap-2 hover:text-[var(--color-accent)] transition-colors">
            <span>✉️</span>
            <span>xlbutorbolt@gmail.com</span>
          </a>
          <div className="flex items-center gap-2">
            <span>🕐</span>
            <span>H-P: 9:00-17:00 | Szo: 8:00-13:00</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-[var(--shadow-subtle)]'
            : 'bg-white'
        }`}
      >
        <nav className="mx-auto max-w-[var(--container-max)] px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="text-2xl font-bold font-[var(--font-serif)] text-[var(--color-text-primary)] tracking-tight">
              XL <span className="text-[var(--color-accent)]">bútor</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium tracking-tight transition-colors relative group ${
                  location.pathname === item.path
                    ? 'text-[var(--color-accent)]'
                    : 'text-[var(--color-text-primary)] hover:text-[var(--color-accent)]'
                }`}
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[var(--color-accent)] transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-[var(--color-accent)] text-white text-sm font-semibold rounded-lg hover:bg-[var(--color-accent-hover)] transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              Ajánlatkérés
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-[var(--color-text-primary)] hover:text-[var(--color-accent)] transition-colors"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            />

            {/* Drawer */}
            <div className="fixed top-0 right-0 bottom-0 w-80 bg-white z-50 shadow-2xl lg:hidden overflow-y-auto">
              <div className="p-6">
                {/* Close button */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-4 right-4 p-2 text-[var(--color-text-primary)] hover:text-[var(--color-accent)]"
                  aria-label="Close menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Logo */}
                <div className="text-2xl font-bold font-[var(--font-serif)] text-[var(--color-text-primary)] mb-8">
                  XL <span className="text-[var(--color-accent)]">bútor</span>
                </div>

                {/* Navigation Links */}
                <div className="flex flex-col gap-4 mb-8">
                  {navItems.map((item) => (
                    <div key={item.path}>
                      <Link
                        to={item.path}
                        className={`block py-3 px-4 rounded-lg text-base font-medium transition-colors ${
                          location.pathname === item.path
                            ? 'bg-[var(--color-accent)] text-white'
                            : 'text-[var(--color-text-primary)] hover:bg-gray-100'
                        }`}
                      >
                        {item.name}
                      </Link>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <Link
                  to="/contact"
                  className="block w-full px-6 py-3 bg-[var(--color-accent)] text-white text-center font-semibold rounded-lg hover:bg-[var(--color-accent-hover)] transition-colors mb-6"
                >
                  Ajánlatkérés
                </Link>

                {/* Contact Info */}
                <div className="pt-6 border-t border-gray-200 space-y-3 text-sm text-[var(--color-text-secondary)]">
                  <a href="tel:06704179210" className="flex items-center gap-3 hover:text-[var(--color-accent)]">
                    <span>📞</span>
                    <span>06 (70) 417 9210</span>
                  </a>
                  <a href="mailto:xlbutorbolt@gmail.com" className="flex items-center gap-3 hover:text-[var(--color-accent)]">
                    <span>✉️</span>
                    <span>xlbutorbolt@gmail.com</span>
                  </a>
                  <div className="flex items-center gap-3">
                    <span>🕐</span>
                    <span>H-P: 9:00-17:00 | Szo: 8:00-13:00</span>
                  </div>
                  <a href="https://www.facebook.com/goncz.sandor.7" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[var(--color-accent)]">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-[var(--color-accent)]">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span>Facebook</span>
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

